import React, { useEffect, useState } from "react";
import Card from "./Card";
import {
  EditCodePoya,
  GetUserInfo,
  ListSanadUser,
  ListUpdate,
} from "../../services/user/user";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import Swal from "sweetalert2";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion"; // Import necessary components
import { convertToJalali } from "../../utils/convertToJalali";
import { useTranslation } from "react-i18next";
function Home() {
  const [isView, setIsView] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [sand, setSand] = useState([]);
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const getInfo = async () => {
    try {
      const res = await GetUserInfo();
      setIsView(res.data.isviewupdate);

      const response = await ListSanadUser();
      if (response.data) {
        setSand(response.data);
      }
    } catch (error) {
      console.error("Error fetching user info or list of sanad:", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    getInfo(); // Fetch user info and other details when the component mounts

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        getInfo(); // Fetch API again when the page is back in view
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleCheckUpdate = async () => {
    try {
      const { data } = await ListUpdate();
      const { version, textupdate } = data;

      const cleanedText = textupdate
        .replace(/\\n/g, "")
        .replace(/\\t/g, "")
        .replace(/&nbsp;/g, " ");

      if (!isView) {
        await Swal.fire({
          title: `${t("Sidebar.Version")} ${version}`,
          confirmButtonColor: "#008A70",
          confirmButtonText: t("Upload.Confirm"),
          html: cleanedText,
          icon: "",
        });
      }
    } catch (error) {
      console.error("Error checking updates:", error);
    }
  };

  useEffect(() => {
    if (isView === false) {
      handleCheckUpdate();
    }
  }, [isView]);

  const deleteSanad = (idstrsanad) => {
    setSand((prevSand) =>
      prevSand.filter((item) => item.idstrsanad !== idstrsanad)
    );
  };

  const updateCodePoya = async (idstrsanad) => {
    try {
      // Make API call to update codepoya
      const response = await EditCodePoya(idstrsanad);

      if (response.value) {
        // Update state with the response from API if no error
        setSand((prevSand) =>
          prevSand.map((item) =>
            item.idstrsanad === idstrsanad
              ? {
                  ...item,
                  codepoya: response.value.codepoya,
                }
              : item
          )
        );
      } else {
        // If there is an error response, remove the item and notify the user
        setSand((prevSand) =>
          prevSand.filter((item) => item.idstrsanad !== idstrsanad)
        );

        // Show an alert notifying that the card is expired
        await Swal.fire({
          icon: "",
          title: t("FileExpired.title"),
          text:  t("FileExpired.text"),
        });
      }
    } catch (error) {
      // Remove the item from the list in case of an error
      setSand((prevSand) =>
        prevSand.filter((item) => item.idstrsanad !== idstrsanad)
      );

      // Show an alert notifying that there was an issue with the card
      await Swal.fire({
        icon: "",
        title: t("Progress.updateError"),
        text: t("Progress.updateErrorT"),
      });
    }
  };

  const handleExpiredSanad = async (idstrsanad) => {
    setSand((prevSand) =>
      prevSand.filter((item) => item.idstrsanad !== idstrsanad)
    );
    await Swal.fire({
      icon: "",
      title: t("FileExpired.title"),
      text:  t("FileExpired.text"),
    });
  };

  const handleRestartTimer = async (idstrsanad) => {
    try {
      const editResponse = await EditCodePoya(idstrsanad);

      if (editResponse.error) {
        handleExpiredSanad(idstrsanad);
        return { success: false };
      }

      const response = await ListSanadUser();
      if (response.data) {
        const updatedData = response.data.map((item) =>
          item.idstrsanad === idstrsanad
            ? {
                ...item,
                remin: 120,
                codepoya: editResponse.value?.codepoya || item.codepoya,
              }
            : item
        );
        setSand(updatedData);
        return { success: true };
      }
      return { success: false };
    } catch (error) {
      console.error("Error restarting timer:", error);
      handleExpiredSanad(idstrsanad);
      return { success: false };
    }
  };

  return (
    <Box className="flex flex-col gap-3 py-4 mb-20">
      {isLoading ? (
        <Box
          sx={{
            height: "50vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="primary" size={70} />
        </Box>
      ) : sand.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "65vh",
          }}
        >
          <img
            src="/images/empty.webp"
            alt="no item found"
            className="mb-2"
            loading="lazy"
          />
          <Typography
            variant="h3"
            color="#636363"
            textAlign="center"
            marginBottom={5}
          >
            {t("Home.emptyTitle")}
          </Typography>
          <Typography variant="h4" color="#636363" textAlign="center">
            {t("Home.emptySubtitle")}
          </Typography>
        </Box>
      ) : (
        <AnimatePresence>
          {sand.map((sanad, index) => (
            <motion.div
              key={sanad.idstrsanad}
              initial={{ opacity: 0, y: 20 }} // Initial animation: fade in + slide from below
              animate={{ opacity: 1, y: 0 }} // Mount animation: fade in + slide to position
              exit={{ opacity: 0, y: -20 }} // Exit animation: fade out + slide up
              transition={{
                duration: 0.5, // Duration for enter animation
                delay: 0.2 * (index + 1), // Add a delay for each subsequent item based on its index
                exit: { duration: 1.5, ease: "easeInOut" }, // Longer duration for exit animation
              }}
            >
              <Card
                key={sanad.idstrsanad}
                namesanad={sanad.namesanad}
                codesanad={sanad.codesanad}
                codepoya={sanad.codepoya}
                datesabt={convertToJalali(sanad.datesabt)}
                idstrsanad={sanad.idstrsanad}
                timeRemaining={sanad.remin || 0} // Pass remin as timeRemaining
                deleteSanad={deleteSanad}
                updateSanad={updateCodePoya}
                onRestartTimer={handleRestartTimer}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      )}
      <Box
        sx={{
          width: "100%",
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "6.5rem", // Place above nav
          display: "flex",
          justifyContent: "start",
          px: 3,
          zIndex: 12,
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate("/upload")}
          sx={{
            padding: "15px 20px",
            borderRadius: "15px",
            border: "1px solid white",
            textTransform: "none",
          }}
        >
          <Typography variant="h5" sx={{ marginRight: "10px" }}>
            {t("Home.Add")}
          </Typography>
          <AddIcon size="large" />
        </Button>
      </Box>
    </Box>
  );
}

export default Home;
